/**
 * SEAL Status Badge — Reusable component
 * Shows SEAL system status in admin header/navbar area.
 *
 * Props:
 *   status: 'active' | 'passive' | 'inactive' | 'error'
 *   mode: 'passive' | 'active' | 'generating'
 *   showLabel: boolean (default true)
 *
 * Self-fetches data from GET /api/seal/config/health on mount,
 * refreshes every 5 minutes.
 */

import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';

const SealStatusBadge = ({ status: propStatus, mode: propMode, showLabel = true }) => {
  const [sealData, setSealData] = useState(null);

  useEffect(() => {
    if (!propStatus) {
      fetchSealStatus();
      const interval = setInterval(fetchSealStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [propStatus]);

  const fetchSealStatus = async () => {
    try {
      const res = await adminApi.get('/api/seal/config/health');
      if (res.data?.success) {
        setSealData(res.data.data);
      }
    } catch (err) {
      setSealData({ error: true });
    }
  };

  const status = propStatus || (sealData?.error ? 'error' : sealData?.seal_enabled ? 'active' : 'inactive');
  const mode = propMode || sealData?.observation_mode || 'passive';

  const getConfig = () => {
    if (status === 'error') return { dot: '#ef4444', label: 'SEAL Error', bg: '#fef2f2', border: '#fecaca' };
    if (status === 'inactive') return { dot: '#ef4444', label: 'SEAL Inactive', bg: '#fef2f2', border: '#fecaca' };
    if (mode === 'generating') return { dot: '#f59e0b', label: 'SEAL Active (Generating)', bg: '#fffbeb', border: '#fde68a' };
    if (mode === 'active') return { dot: '#10b981', label: 'SEAL Active', bg: '#ecfdf5', border: '#a7f3d0' };
    return { dot: '#10b981', label: 'SEAL Active (Passive)', bg: '#ecfdf5', border: '#a7f3d0' };
  };

  const config = getConfig();

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      borderRadius: '12px',
      backgroundColor: config.bg,
      border: `1px solid ${config.border}`,
      fontSize: '12px',
      fontWeight: 500,
      cursor: 'default'
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: config.dot,
        display: 'inline-block',
        animation: status === 'active' ? 'sealPulse 2s ease-in-out infinite' : 'none'
      }} />
      {showLabel && <span style={{ color: '#374151' }}>{config.label}</span>}
      <style>{`
        @keyframes sealPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default SealStatusBadge;
